'use client'

import { useEffect } from 'react'
import { useRouter, useSearchParams } from '@/lib/next-shim'

import { GetMyInfosQuery } from '@/core/graphql/types'
import { useNavigateOnce } from '@/hooks/useNavigateOnce'
import { sanitizeCallbackUrl } from '@/utils/auth'
import { isPatientReady } from '@/utils/profile-readiness'

type UseCompleteProfileRedirectsParams = {
  status: string
  patient: GetMyInfosQuery['myInfos'] | undefined
  isFrance: boolean
}

/**
 * Owns the two navigations away from the complete-profile page:
 * - unauthenticated -> login (preserving the return path)
 * - already bill-ready -> the callback (nothing left to complete)
 */
export function useCompleteProfileRedirects({ status, patient, isFrance }: UseCompleteProfileRedirectsParams) {
  const navigate = useNavigateOnce()
  const router = useRouter()
  const searchParams = useSearchParams()

  const callbackUrl = sanitizeCallbackUrl(searchParams?.get('callbackUrl'))

  useEffect(() => {
    if (status === 'unauthenticated') {
      const returnUrl = `/complete-profile?callbackUrl=${encodeURIComponent(callbackUrl)}`
      router.replace(`/login?callbackUrl=${encodeURIComponent(returnUrl)}`)
    }
  }, [status, router, callbackUrl])

  useEffect(() => {
    if (patient && isPatientReady(patient, isFrance)) {
      navigate(callbackUrl)
    }
  }, [patient, isFrance, callbackUrl, navigate])
}
